import React from "react";
import { Clock, Search } from "lucide-react";
import VillePicker from "./VillePicker";

const nextTarget = (s) => {
  const villes = s.search_villes?.length ? s.search_villes : ["Hauts-de-France"];
  const sectors = s.search_sectors?.length ? s.search_sectors : ["tertiaire"];
  const cur = s.search_cursor || 0;
  return `${villes[cur % villes.length]} (${sectors[Math.floor(cur / villes.length) % sectors.length]})`;
};

const DAYS = [
  { i: 0, l: "Lun" }, { i: 1, l: "Mar" }, { i: 2, l: "Mer" },
  { i: 3, l: "Jeu" }, { i: 4, l: "Ven" }, { i: 5, l: "Sam" }, { i: 6, l: "Dim" },
];
const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-neutral-600 mb-1">{label}</label>
    {children}
  </div>
);
const splitList = (v) => v.split(",").map((x) => x.trim()).filter(Boolean);

export const ScheduleSettings = ({ s, patch }) => {
  const toggleDay = (i) => patch("days", s.days.includes(i) ? s.days.filter((d) => d !== i) : [...s.days, i].sort());
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="autopilot-schedule">
      <h3 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2"><Clock size={16} className="text-brand" /> Plage horaire & cadence</h3>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Début"><input type="time" className={inputCls} value={s.window_start} onChange={(e) => patch("window_start", e.target.value)} /></Field>
        <Field label="Fin"><input type="time" className={inputCls} value={s.window_end} onChange={(e) => patch("window_end", e.target.value)} /></Field>
      </div>
      <label className="block text-xs font-semibold text-neutral-600 mb-2">Jours autorisés</label>
      <div className="flex flex-wrap gap-2 mb-4">
        {DAYS.map((d) => (
          <button key={d.i} onClick={() => toggleDay(d.i)} className={`px-3 py-1.5 rounded-md text-sm font-medium border ${s.days.includes(d.i) ? "bg-brand text-white border-brand" : "border-neutral-300 text-neutral-600"}`}>
            {d.l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max emails / jour"><input type="number" min={1} max={200} className={inputCls} value={s.daily_cap} onChange={(e) => patch("daily_cap", Number(e.target.value))} /></Field>
        <Field label="Délai min. (sec)"><input type="number" min={10} className={inputCls} value={s.min_gap_seconds} onChange={(e) => patch("min_gap_seconds", Number(e.target.value))} /></Field>
        <Field label="Relance après (jours)"><input type="number" min={1} className={inputCls} value={s.relance_after_days} onChange={(e) => patch("relance_after_days", Number(e.target.value))} /></Field>
      </div>
    </div>
  );
};

export const SearchSettings = ({ s, patch }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="autopilot-search">
    <h3 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2"><Search size={16} className="text-brand" /> Recherche automatique d'entreprises</h3>
    <label className="flex items-center gap-2 text-sm text-neutral-700 mb-4">
      <input type="checkbox" checked={s.auto_search_enabled} onChange={(e) => patch("auto_search_enabled", e.target.checked)} className="accent-brand w-4 h-4" />
      Activer la recherche web automatique (Gemini)
    </label>
    <div className="mb-3">
      <Field label="Secteurs ciblés (séparés par virgule)"><input className={inputCls} value={(s.search_sectors || []).join(", ")} onChange={(e) => patch("search_sectors", splitList(e.target.value))} /></Field>
    </div>
    <div className="mb-3">
      <Field label="Villes ciblées — proposées automatiquement autour du siège">
        <VillePicker selected={s.search_villes || []} onChange={(v) => patch("search_villes", v)} radius={s.search_radius_km || 40} setRadius={(r) => patch("search_radius_km", r)} nextVille={nextTarget(s)} />
      </Field>
    </div>
    <div className="mb-3">
      <Field label="Villes sélectionnées (modifiable, séparées par virgule)"><input className={inputCls} value={(s.search_villes || []).join(", ")} onChange={(e) => patch("search_villes", splitList(e.target.value))} data-testid="autopilot-villes-input" /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Entreprises / recherche"><input type="number" min={3} max={15} className={inputCls} value={s.search_per_run} onChange={(e) => patch("search_per_run", Number(e.target.value))} /></Field>
      <Field label="Seuil pool min."><input type="number" min={0} className={inputCls} value={s.min_pool} onChange={(e) => patch("min_pool", Number(e.target.value))} /></Field>
    </div>
    <label className="flex items-center gap-2 text-sm text-neutral-700 mt-4">
      <input type="checkbox" checked={s.unsubscribe} onChange={(e) => patch("unsubscribe", e.target.checked)} className="accent-brand w-4 h-4" />
      Ajouter la mention de désinscription (RGPD)
    </label>
  </div>
);
